import React, { forwardRef, useMemo } from 'react';

import { SerializedStyles } from '@emotion/react';
import { Box, Typography, Container } from '@mui/material';
import { Theme } from '@mui/material/styles';
import cx from 'classnames';

import { HEADER_IMG_SRC_SET } from '../../const';
import styled from '../../styled';
import { generateRandomImageUrl } from '../../utils/helpers';
import IKImage from '../IKImage/IKImage';
import useStyles from './style';

interface HeaderProps {
  title?: string;
  children?: React.ReactNode;
  imageUrl?: string;
  imageSrcset?: any[];
  image?: any;
  wrapperClass?: string;
  contentWrapperClass?: string;
  imageWrapperClass?: string;
  titleClass?: string;
  tenantName?: string | null;
  wrapperCss?: SerializedStyles;
  contentWrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  titleCss?: SerializedStyles;
}

const Header = forwardRef<HTMLDivElement, HeaderProps>(
  (
    {
      title,
      children,
      imageUrl,
      imageSrcset = HEADER_IMG_SRC_SET,
      image,
      wrapperClass = '',
      contentWrapperClass = '',
      titleClass,
      imageWrapperClass,
      tenantName,
      wrapperCss,
      contentWrapperCss,
      titleCss,
    },
    ref,
  ) => {
    const currentImage = useMemo(() => generateRandomImageUrl(tenantName), [tenantName]);

    const classes = useStyles(true)();

    return (
      <Wrapper ref={ref} className={cx(classes.wrapper, wrapperClass)} optionalStyles={wrapperCss}>
        <Box className={cx(classes.imageWrapper, imageWrapperClass)}>
          {image ? (
            <img src={image} alt={title} className={classes.img} />
          ) : (
            <IKImage
              className={classes.img}
              path={imageUrl || currentImage}
              alt={title}
              srcset={imageSrcset}
              sizes="100vw"
              draggable={false}
            />
          )}
        </Box>
        <Box className={classes.defaultBackground} />
        <ContentWrapper
          className={cx(classes.contentWrapper, contentWrapperClass)}
          optionalStyles={contentWrapperCss}
        >
          {title && (
            <Container>
              <Title
                data-testid="pageTitle"
                className={cx(classes.title, titleClass, 'headerTitle')}
                variant="h1"
                optionalStyles={titleCss}
              >
                {title}
              </Title>
            </Container>
          )}
          {children}
        </ContentWrapper>
      </Wrapper>
    );
  },
);

export default Header;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  ${({ optionalStyles }) => optionalStyles}
`;
const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  // TODO: remove '&&' after full migration Header component to emotion
  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  // TODO: remove '&&' after full migration Header component to emotion
  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
